import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Truck, RefreshCw, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as notificationApi from '@/services/notificationApi';
import * as authApi from '@/services/authApi';

const ShipmentRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [showBundleModal, setShowBundleModal] = useState(false);
  const [selectedBundles, setSelectedBundles] = useState({ bundles: [], document_barcode: '' });
  const [confirmingId, setConfirmingId] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchUser();
    fetchRequests();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (user) {
      setupWebSocket();
    }
  }, [user]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
      navigate('/login');
    }
  };

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const data = await notificationApi.getShipmentRequests();
      setRequests(data.consignments || []);
    } catch (error) {
      console.error('Error fetching shipment requests:', error);
      toast.error('Failed to load shipment requests');
    } finally {
      setIsLoading(false);
    }
  };

  const setupWebSocket = () => {
    if (!user) return;
    
    // Notification sound
    const notificationSound = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telehmg1/NjIQAsicHQpJ4cLouu8JHCqWUEADqk0dNdEDxgktzS12xwLxJPl8/Mxc/I2JYWBFltsrHd1dvgixwBRWWQoOjv0uKQMwAtXm+X1e3d0qx0GgM5X4ed5vHZ3YElBy1ler3k6+HYkjkLIVN3m9vu6tyYRBEeTnGV3O7n26FPGhZIZ4m94+zg2plOGQ9CYoiy4uzc2ppSHQ0+YIWw4uva2ZtTHgs6Xoau4uvZ2JtUHwo4XYSs4uvY15xVIAg2XIKq4erX1p1WIQczW4Go4enW1Z5XIwYxWoCn4OnV1J9YJAQvWX+l3+jU05xZJQMuWH+k3+fT0p1aJgItV36j3ubS0Z5bJgEsV32i3eXR0J9cJwArVnyh3OTQz6BdKAApVXuf2+PPzqFeKAAoVHqe2uLOzaJfKgAnU3md2eHNzKNgKgAmUnib2ODMy6RhKwAlUXea19/LyqViLAAkUHaZ1t7KyaZjLQAjT3WY1d3JyKdkLgAiTnSX1NzIx6hlLwAhTXOW09vHxqlmMAAhTHKV0trGxapoMQAgS3GU0dnFxKtpMgAfSnCTz9jEw6xqMwAeSm+Sztf');
    
    wsRef.current = notificationApi.createNotificationWebSocket(
      user.user_id,
      (data) => {
        if (data.type === 'new_notification') {
          // Play sound
          notificationSound.play().catch(() => {});
          
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(data.notification.title, {
              body: data.notification.message,
              icon: '/favicon.ico'
            });
          }
          
          // Refresh requests if relevant notification
          if (data.notification.type === 'consignment_requested') {
            fetchRequests();
          }
          
          toast.info(data.notification.title);
        }
      },
      (error) => {
        console.error('WebSocket error:', error);
      }
    );
  };

  const handleConfirmShipment = async (entryId) => {
    setConfirmingId(entryId);
    try {
      await notificationApi.confirmShipment(entryId);
      toast.success('Shipment confirmed successfully');
      fetchRequests();
    } catch (error) {
      console.error('Error confirming shipment:', error);
      toast.error(error.response?.data?.detail || 'Failed to confirm shipment');
    } finally {
      setConfirmingId(null);
    }
  };

  const handleViewBundles = async (entryId) => {
    try {
      const data = await notificationApi.getConsignmentBundles(entryId);
      setSelectedBundles(data);
      setShowBundleModal(true);
    } catch (error) {
      console.error('Error fetching bundles:', error);
      toast.error('Failed to load bundles');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#F9FAFB] hover:bg-white/10">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Truck className="w-6 h-6" />
              <h1 className="text-xl font-bold font-display">Shipment Requests</h1>
            </div>
          </div>
          <Button onClick={fetchRequests} variant="secondary" className="h-10">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-6">
          <h2 className="text-lg font-semibold text-[#1A1F2E] mb-6 font-display">
            Pending Shipment Requests
          </h2>
          
          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2D5F4F] mx-auto" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12">
              <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No pending shipment requests</p>
            </div>
          ) : (
            <div className="border border-[#E5DFD0] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F5F1E8] hover:bg-[#F5F1E8]">
                      <TableHead className="font-semibold text-[#1A1F2E]">Document Barcode</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Section Name</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Bundle Count</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Requested By</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E]">Requested At</TableHead>
                      <TableHead className="font-semibold text-[#1A1F2E] text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((request) => (
                      <TableRow key={request.entry_id} className="hover:bg-[#FFFBF5]">
                        <TableCell className="font-medium text-[#1A1F2E]">
                          {request.document_barcode}
                        </TableCell>
                        <TableCell>{request.section_name}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleViewBundles(request.entry_id)}
                            className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            data-testid={`view-bundles-${request.entry_id}`}
                          >
                            {request.bundle_count || 0}
                          </button>
                        </TableCell>
                        <TableCell>{request.requested_by_name || '-'}</TableCell>
                        <TableCell className="text-sm">
                          {formatDate(request.requested_at)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            onClick={() => handleConfirmShipment(request.entry_id)}
                            size="sm"
                            disabled={confirmingId === request.entry_id}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`confirm-shipment-${request.entry_id}`}
                          >
                            {confirmingId === request.entry_id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm Shipment
                              </>
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Bundle Modal */}
      <Dialog open={showBundleModal} onOpenChange={setShowBundleModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bundle QR Codes - {selectedBundles.document_barcode}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedBundles.bundles && selectedBundles.bundles.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {selectedBundles.bundles.map((bundle, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-[#F5F1E8] rounded-lg text-sm font-mono"
                  >
                    {bundle}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-[#5A5F6E]">No bundles found</p>
            )}
            <p className="mt-4 text-sm text-[#5A5F6E]">
              Total Bundles: <strong>{selectedBundles.bundles?.length || 0}</strong>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShipmentRequests;
